import { useState } from "react";
import {
  CaretUp, CaretDown, TextT, Target, Lightbulb, CheckCircle, Trophy,
  ChatCircleDots, Question, Rocket, Plus, X, Warning, CaretRight, Image as ImageIcon,
  Star, Heart, Sparkle, Lightning, ShieldCheck, Users, ChartBar, ChartLine,
  Robot, Brain, Cpu, Cloud, Database, Code, Globe, EnvelopeSimple, Phone,
  Calendar, Clock, Gift, Handshake, Gauge, Graph, Coins, MagnifyingGlass,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

// Media Phase 1 — Curated Phosphor set mirroring backend LP_ALLOWED_ICONS.
// Any icon not in this map is rejected server-side, so the picker is the
// single source of truth for the admin UX.
export const LP_MEDIA_ICONS = {
  Rocket, Target, Lightbulb, CheckCircle, Trophy, Star, Heart, Sparkle,
  Lightning, ShieldCheck, Users, ChartBar, ChartLine, Robot, Brain, Cpu,
  Cloud, Database, Code, Globe, EnvelopeSimple, Phone, Calendar, Clock,
  Gift, Handshake, Gauge, Graph, Coins, MagnifyingGlass,
};
const LP_MEDIA_PLACEMENTS = [
  { v: "below", label: "Below content" },
  { v: "left", label: "Left of content" },
  { v: "right", label: "Right of content" },
  { v: "center", label: "Above content (centered)" },
];
const LP_MEDIA_DISPLAY_STYLES = [
  { v: "contained", label: "Contained" },
  { v: "full_width", label: "Full width" },
  { v: "card", label: "Card (bordered)" },
];

// Reusable LP System · Phase 1 — Section builder.
// Renders the 8 core sections with toggle/reorder/inline editor.
// Design intentional: sections list is presentation-agnostic — adding a new
// section type later means adding an entry to SECTION_META + an editor.

const SECTION_META = {
  hero:         { label: "Hero",          Icon: TextT,        blurb: "Headline + subheadline. Migrated from your existing fields." },
  problem:      { label: "Problem",       Icon: Target,       blurb: "The pain you solve — one short paragraph." },
  solution:     { label: "Solution",      Icon: Lightbulb,    blurb: "How you solve it — one short paragraph." },
  benefits:     { label: "Benefits",      Icon: CheckCircle,  blurb: "Bulleted outcomes. Migrated from your existing benefits." },
  proof:        { label: "Proof / Case",  Icon: Trophy,       blurb: "A short case study block: title, description, optional metric." },
  testimonials: { label: "Testimonials",  Icon: ChatCircleDots, blurb: "Quote + name + title. Migrated from your existing testimonials." },
  faq:          { label: "FAQ",           Icon: Question,     blurb: "Question / answer pairs." },
  cta:          { label: "Final CTA",     Icon: Rocket,       blurb: "One last hook — links to the embedded form." },
  how_it_works: { label: "How It Works",  Icon: Gauge,        blurb: "Numbered steps that walk visitors through your process." },
};

export default function LandingPageSections({ sections, onChange }) {
  const [expanded, setExpanded] = useState({});

  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= sections.length) return;
    const next = [...sections];
    [next[idx], next[to]] = [next[to], next[idx]];
    onChange(next.map((s, i) => ({ ...s, order: i })));
  };

  const toggle = (idx, enabled) => {
    onChange(sections.map((s, i) => i === idx ? { ...s, enabled } : s));
  };

  const patchContent = (idx, patch, sectionPatch) => {
    onChange(sections.map((s, i) =>
      i === idx
        ? { ...s, ...(sectionPatch || {}), content: { ...(s.content || {}), ...(patch || {}) } }
        : s
    ));
  };

  const enabledCount = sections.filter(s => s.enabled).length;

  return (
    <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4" data-testid="lp-sections">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-ink-secondary">Sections</div>
          <div className="mt-0.5 text-[11px] text-ink-secondary/70" data-testid="lp-sections-summary">
            {enabledCount} of {sections.length} enabled · reorder with arrows
          </div>
        </div>
        {enabledCount === 0 && (
          <div className="inline-flex items-center gap-1.5 text-[11px] text-amber-200 border border-amber-400/25 bg-amber-400/5 rounded-full px-3 py-1"
               data-testid="lp-sections-none-enabled">
            <Warning weight="duotone" className="h-3 w-3" />
            Enable at least one section
          </div>
        )}
      </div>

      <ul className="space-y-2">
        {sections.map((s, idx) => {
          const meta = SECTION_META[s.section_type] || { label: s.section_type, Icon: TextT, blurb: "" };
          const Icon = meta.Icon;
          const isOpen = !!expanded[s.section_id];
          return (
            <li key={s.section_id}
                data-testid={`lp-section-${s.section_type}`}
                data-enabled={s.enabled ? "true" : "false"}
                data-order={s.order}
                className="rounded-md border border-white/10 bg-graphite-900">
              <div className="flex items-center gap-2 p-3">
                <div className="flex flex-col">
                  <button
                    type="button"
                    data-testid={`lp-section-${s.section_type}-up`}
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="p-1 rounded text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent"
                  ><CaretUp weight="bold" className="h-3 w-3" /></button>
                  <button
                    type="button"
                    data-testid={`lp-section-${s.section_type}-down`}
                    onClick={() => move(idx, 1)}
                    disabled={idx === sections.length - 1}
                    className="p-1 rounded text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent"
                  ><CaretDown weight="bold" className="h-3 w-3" /></button>
                </div>
                <button
                  type="button"
                  onClick={() => setExpanded(e => ({ ...e, [s.section_id]: !e[s.section_id] }))}
                  data-testid={`lp-section-${s.section_type}-toggle-editor`}
                  className="flex-1 flex items-center gap-3 text-left"
                >
                  <Icon weight="duotone" className="h-4 w-4 text-ink-primary/80" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-ink-primary">{meta.label}</div>
                    <div className="text-[11px] text-ink-secondary truncate">{meta.blurb}</div>
                  </div>
                  <CaretRight weight="bold"
                    className={`h-3 w-3 text-ink-secondary transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-ink-secondary">
                    {s.enabled ? "On" : "Off"}
                  </span>
                  <Switch
                    data-testid={`lp-section-${s.section_type}-enabled`}
                    checked={!!s.enabled}
                    onCheckedChange={(v) => toggle(idx, v)}
                  />
                </div>
              </div>
              {isOpen && (
                <div data-testid={`lp-section-${s.section_type}-editor`} className="px-4 pb-4 border-t border-white/5">
                  <SectionEditor section={s} onPatch={(patch, sectionPatch) => patchContent(idx, patch, sectionPatch)} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const VARIANT_LABELS = {
  hero:     { split: "Split (default)",       centered: "Centered",       stat_led: "Stat-led" },
  problem:  { cards: "Cards (default)",       stat: "Big stat" },
  solution: { cards: "Cards (default)" },
  benefits: { icon_cards: "Icon cards (default)", outcomes: "Outcomes (number-led)" },
  proof:    { case_study: "Case study (default)" },
  cta:      { standard: "Standard (default)", offer: "With offer box" },
  how_it_works: { numbered_steps: "Numbered steps (default)", horizontal: "Horizontal row", vertical: "Vertical timeline" },
};

function VariantPicker({ sectionType, value, onChange }) {
  const map = VARIANT_LABELS[sectionType];
  if (!map) return null;
  const options = Object.entries(map);
  if (options.length < 2) return null;
  const current = value || options[0][0];
  return (
    <div className="mt-3">
      <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Layout variant</label>
      <select
        data-testid={`lp-section-${sectionType}-variant`}
        value={current}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30"
      >
        {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
    </div>
  );
}

function SectionEditor({ section, onPatch }) {
  const c = section.content || {};
  const t = section.section_type;
  const variantPicker = (
    <VariantPicker
      sectionType={t}
      value={section.variant}
      onChange={(v) => onPatch({}, { variant: v })}
    />
  );
  const mediaEditor = (
    <MediaEditor
      sectionType={t}
      value={section.media}
      onChange={(m) => onPatch({}, { media: m })}
    />
  );

  if (t === "hero") {
    return (
      <div className="mt-3 space-y-3">
        {variantPicker}
        <Field label="Headline" required>
          <Input data-testid="lp-hero-headline" value={c.headline || ""} maxLength={200}
            onChange={e => onPatch({ headline: e.target.value })}
            className="bg-graphite-950 border-white/10" />
        </Field>
        <Field label="Subheadline">
          <Input data-testid="lp-hero-subheadline" value={c.subheadline || ""} maxLength={300}
            onChange={e => onPatch({ subheadline: e.target.value })}
            className="bg-graphite-950 border-white/10" />
        </Field>
        {section.variant === "stat_led" && (
          <>
            <Field label="Stat value (e.g. 3×, 42%, $180k)">
              <Input data-testid="lp-hero-stat-value" value={c.stat_value || ""} maxLength={300}
                onChange={e => onPatch({ stat_value: e.target.value })}
                className="bg-graphite-950 border-white/10" />
            </Field>
            <Field label="Stat label (short description)">
              <Input data-testid="lp-hero-stat-label" value={c.stat_label || ""} maxLength={300}
                onChange={e => onPatch({ stat_label: e.target.value })}
                className="bg-graphite-950 border-white/10" />
            </Field>
          </>
        )}
        <Field label='CTA button text (used when form is revealed on click)'>
          <Input data-testid="lp-hero-cta-text" value={c.cta_text || ""} maxLength={300}
            placeholder="See How AI Could Improve Your Business"
            onChange={e => onPatch({ cta_text: e.target.value })}
            className="bg-graphite-950 border-white/10" />
        </Field>
        {mediaEditor}
      </div>
    );
  }

  if (t === "problem" || t === "solution") {
    return (
      <div className="mt-3 space-y-3">
        {variantPicker}
        <Field label="Heading (optional)">
          <Input data-testid={`lp-${t}-heading`} value={c.heading || ""} maxLength={300}
            onChange={e => onPatch({ heading: e.target.value })}
            className="bg-graphite-950 border-white/10" />
        </Field>
        <Field label="Body">
          <Textarea data-testid={`lp-${t}-body`} value={c.body || ""} rows={4} maxLength={3000}
            onChange={e => onPatch({ body: e.target.value })}
            className="bg-graphite-950 border-white/10" />
        </Field>
        {t === "problem" && section.variant === "stat" && (
          <>
            <Field label="Big stat (e.g. 60% of leads never respond)">
              <Input data-testid="lp-problem-stat-value" value={c.stat_value || ""} maxLength={300}
                onChange={e => onPatch({ stat_value: e.target.value })}
                className="bg-graphite-950 border-white/10" />
            </Field>
            <Field label="Stat label">
              <Input data-testid="lp-problem-stat-label" value={c.stat_label || ""} maxLength={300}
                onChange={e => onPatch({ stat_label: e.target.value })}
                className="bg-graphite-950 border-white/10" />
            </Field>
          </>
        )}
        {mediaEditor}
      </div>
    );
  }

  if (t === "benefits") {
    return (
      <div className="mt-3 space-y-3">
        {variantPicker}
        <ListEditor
          testidPrefix="lp-benefits"
          heading={c.heading}
          onHeadingChange={(v) => onPatch({ heading: v })}
          items={c.items || []}
          onItemsChange={(items) => onPatch({ items })}
          itemPlaceholder={section.variant === "outcomes" ? "e.g. 3× reactivation rate" : "e.g. Setup in under 10 minutes"}
          itemMax={200}
          max={20}
        />
        {mediaEditor}
      </div>
    );
  }

  if (t === "proof") {
    return (
      <div className="mt-3 space-y-3">
        {variantPicker}
        <Field label="Heading (optional)">
          <Input data-testid="lp-proof-heading" value={c.heading || ""} maxLength={300}
            onChange={e => onPatch({ heading: e.target.value })}
            className="bg-graphite-950 border-white/10" />
        </Field>
        <Field label="Case study title">
          <Input data-testid="lp-proof-title" value={c.title || ""} maxLength={300}
            onChange={e => onPatch({ title: e.target.value })}
            className="bg-graphite-950 border-white/10" />
        </Field>
        <Field label="Description">
          <Textarea data-testid="lp-proof-description" value={c.description || ""} rows={4} maxLength={3000}
            onChange={e => onPatch({ description: e.target.value })}
            className="bg-graphite-950 border-white/10" />
        </Field>
        <Field label="Metric (optional)">
          <Input data-testid="lp-proof-metric" value={c.metric || ""} maxLength={120}
            onChange={e => onPatch({ metric: e.target.value })}
            className="bg-graphite-950 border-white/10" />
        </Field>
        {mediaEditor}
      </div>
    );
  }

  if (t === "cta") {
    return (
      <div className="mt-3 space-y-3">
        {variantPicker}
        <Field label="Headline">
          <Input data-testid="lp-cta-headline" value={c.headline || ""} maxLength={200}
            onChange={e => onPatch({ headline: e.target.value })}
            className="bg-graphite-950 border-white/10" />
        </Field>
        <Field label="Button text">
          <Input data-testid="lp-cta-button" value={c.button_text || ""} maxLength={300}
            placeholder="Get started"
            onChange={e => onPatch({ button_text: e.target.value })}
            className="bg-graphite-950 border-white/10" />
        </Field>
        {section.variant === "offer" && (
          <Field label="Offer / incentive box text">
            <Textarea data-testid="lp-cta-offer" value={c.offer_text || ""} rows={2} maxLength={3000}
              onChange={e => onPatch({ offer_text: e.target.value })}
              placeholder="Book by Friday for a free strategy call"
              className="bg-graphite-950 border-white/10" />
          </Field>
        )}
        {mediaEditor}
      </div>
    );
  }

  if (t === "testimonials") {
    return (
      <div className="mt-3 space-y-3">
        <TestimonialsEditor
          heading={c.heading}
          onHeadingChange={(v) => onPatch({ heading: v })}
          items={c.items || []}
          onItemsChange={(items) => onPatch({ items })}
        />
        {mediaEditor}
      </div>
    );
  }

  if (t === "faq") {
    return (
      <div className="mt-3 space-y-3">
        <FaqEditor
          heading={c.heading}
          onHeadingChange={(v) => onPatch({ heading: v })}
          items={c.items || []}
          onItemsChange={(items) => onPatch({ items })}
        />
        {mediaEditor}
      </div>
    );
  }

  if (t === "how_it_works") {
    return (
      <div className="mt-3 space-y-3">
        {variantPicker}
        <Field label="Heading (optional)">
          <Input data-testid="lp-how-heading" value={c.heading || ""} maxLength={300}
            onChange={e => onPatch({ heading: e.target.value })}
            className="bg-graphite-950 border-white/10" />
        </Field>
        <StepsEditor steps={c.steps || []} onChange={(steps) => onPatch({ steps })} />
        {mediaEditor}
      </div>
    );
  }

  return null;
}

function LogoStripEditor({ logos, onChange }) {
  const [draft, setDraft] = useState({ name: "", url: "" });
  const add = () => {
    const url = draft.url.trim();
    if (!url) return;
    onChange([...logos, { name: draft.name.trim(), url }]);
    setDraft({ name: "", url: "" });
  };
  const remove = (i) => onChange(logos.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid="lp-proof-logos-editor">
      <div className="text-[11px] uppercase tracking-widest text-ink-secondary">Client logos</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input data-testid="lp-proof-logo-name" value={draft.name} maxLength={200}
          onChange={e => setDraft({ ...draft, name: e.target.value })}
          placeholder="Client name" className="bg-graphite-950 border-white/10 text-sm" />
        <Input data-testid="lp-proof-logo-url" value={draft.url} maxLength={1000}
          onChange={e => setDraft({ ...draft, url: e.target.value })}
          placeholder="https://.../logo.svg" className="bg-graphite-950 border-white/10 text-sm" />
      </div>
      <button type="button" data-testid="lp-proof-logo-add" onClick={add}
        disabled={!draft.url.trim()}
        className="inline-flex items-center gap-1.5 rounded-full bg-white text-graphite-950 px-3 py-1.5 text-xs font-medium hover:bg-neutral-100 disabled:opacity-60">
        Add logo
      </button>
      <ul className="space-y-1.5">
        {logos.map((l, i) => (
          <li key={i} data-testid={`lp-proof-logo-item-${i}`}
              className="flex items-center gap-2 rounded-md border border-white/10 bg-graphite-950 px-3 py-2">
            <img src={l.url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
                 className="h-6 w-auto max-w-[80px] object-contain" />
            <span className="flex-1 text-xs text-ink-primary truncate">{l.name || l.url}</span>
            <button type="button" onClick={() => remove(i)} data-testid={`lp-proof-logo-remove-${i}`}
              className="text-ink-secondary hover:text-accent-danger">
              <X weight="bold" className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">
        {label} {required && <span className="text-amber-300">*</span>}
      </label>
      {children}
    </div>
  );
}

function ListEditor({ testidPrefix, heading, onHeadingChange, items, onItemsChange, itemPlaceholder, itemMax, max }) {
  // Always-editable pattern — inputs bound directly to items[i], no
  // staging `draft` state that could be discarded on outer Save without
  // clicking a separate Add button first.
  const rows = items.length > 0 ? items : [""];
  const patch = (i, val) => {
    const base = items.length > 0 ? items : rows;
    onItemsChange(base.map((v, idx) => idx === i ? val : v));
  };
  const remove = (i) => {
    const next = rows.filter((_, idx) => idx !== i);
    onItemsChange(next);   // OK if empty — parent decides what to do
  };
  const add = () => {
    if (rows.length >= max) return;
    onItemsChange([...rows, ""]);
  };
  return (
    <div className="mt-3 space-y-3">
      <Field label="Heading (optional)">
        <Input data-testid={`${testidPrefix}-heading`} value={heading || ""} maxLength={300}
          onChange={e => onHeadingChange(e.target.value)}
          className="bg-graphite-950 border-white/10" />
      </Field>
      <ul className="space-y-1.5">
        {rows.map((b, i) => (
          <li key={i} data-testid={`${testidPrefix}-item-${i}`}
              className="flex items-center gap-2 rounded-md border border-white/10 bg-graphite-950 px-3 py-2">
            <Input data-testid={`${testidPrefix}-input-${i}`} value={b || ""}
              onChange={e => patch(i, e.target.value)}
              maxLength={itemMax}
              placeholder={itemPlaceholder}
              className="bg-graphite-900 border-white/10 text-sm flex-1" />
            <button type="button" data-testid={`${testidPrefix}-remove-${i}`} onClick={() => remove(i)}
              className="text-ink-secondary hover:text-accent-danger" aria-label="Remove item">
              <X weight="bold" className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
      {rows.length < max && (
        <button type="button" data-testid={`${testidPrefix}-add`} onClick={add}
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-graphite-950 px-3 py-1.5 text-xs font-medium hover:bg-neutral-100">
          <Plus weight="bold" className="h-3 w-3" /> Add another
        </button>
      )}
    </div>
  );
}

function TestimonialsEditor({ heading, onHeadingChange, items, onItemsChange }) {
  // Always-editable pattern — inputs bound directly to items[i].{quote,name,title}.
  // Previously used a staging draft + "Add testimonial" button that discarded
  // typed data if the user hit outer Save without clicking Add first.
  const rows = items.length > 0 ? items : [{ quote: "", name: "", title: "" }];
  const patch = (i, key, val) => {
    const base = items.length > 0 ? items : rows;
    onItemsChange(base.map((t, idx) => idx === i ? { ...t, [key]: val } : t));
  };
  const remove = (i) => onItemsChange(rows.filter((_, idx) => idx !== i));
  const add = () => onItemsChange([...rows, { quote: "", name: "", title: "" }]);
  return (
    <div className="mt-3 space-y-3">
      <Field label="Heading (optional)">
        <Input data-testid="lp-testimonials-heading" value={heading || ""} maxLength={300}
          onChange={e => onHeadingChange(e.target.value)}
          className="bg-graphite-950 border-white/10" />
      </Field>
      <ul className="space-y-2">
        {rows.map((t, i) => (
          <li key={i} data-testid={`lp-testimonials-item-${i}`}
              className="rounded-md border border-white/10 bg-graphite-950 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <ChatCircleDots weight="duotone" className="h-4 w-4 text-ink-secondary shrink-0 mt-1.5" />
              <Textarea data-testid={`lp-testimonials-quote-${i}`} value={t.quote || ""}
                onChange={e => patch(i, "quote", e.target.value)}
                rows={2} placeholder='"They tripled our reactivation rate."'
                className="flex-1 bg-graphite-900 border-white/10 text-sm" />
              <button type="button" data-testid={`lp-testimonials-remove-${i}`} onClick={() => remove(i)}
                className="text-ink-secondary hover:text-accent-danger mt-1" aria-label="Remove testimonial">
                <X weight="bold" className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-6">
              <Input data-testid={`lp-testimonials-name-${i}`} value={t.name || ""}
                onChange={e => patch(i, "name", e.target.value)}
                placeholder="Name (e.g. Jane Doe)" className="bg-graphite-900 border-white/10 text-sm" />
              <Input data-testid={`lp-testimonials-title-${i}`} value={t.title || ""}
                onChange={e => patch(i, "title", e.target.value)}
                placeholder="Title (e.g. CEO, Acme Inc)" className="bg-graphite-900 border-white/10 text-sm" />
            </div>
          </li>
        ))}
      </ul>
      <button type="button" data-testid="lp-testimonials-add" onClick={add}
        className="inline-flex items-center gap-1.5 rounded-full bg-white text-graphite-950 px-3 py-1.5 text-xs font-medium hover:bg-neutral-100">
        <Plus weight="bold" className="h-3 w-3" /> Add testimonial
      </button>
    </div>
  );
}

function FaqEditor({ heading, onHeadingChange, items, onItemsChange }) {
  // Always-editable — no staging draft.
  const rows = items.length > 0 ? items : [{ question: "", answer: "" }];
  const patch = (i, key, val) => {
    const base = items.length > 0 ? items : rows;
    onItemsChange(base.map((qa, idx) => idx === i ? { ...qa, [key]: val } : qa));
  };
  const remove = (i) => onItemsChange(rows.filter((_, idx) => idx !== i));
  const add = () => onItemsChange([...rows, { question: "", answer: "" }]);
  return (
    <div className="mt-3 space-y-3">
      <Field label="Heading (optional)">
        <Input data-testid="lp-faq-heading" value={heading || ""} maxLength={300}
          onChange={e => onHeadingChange(e.target.value)}
          className="bg-graphite-950 border-white/10" />
      </Field>
      <ul className="space-y-2">
        {rows.map((qa, i) => (
          <li key={i} data-testid={`lp-faq-item-${i}`}
              className="rounded-md border border-white/10 bg-graphite-950 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <Input data-testid={`lp-faq-question-${i}`} value={qa.question || ""}
                onChange={e => patch(i, "question", e.target.value)}
                placeholder="Question" maxLength={240}
                className="flex-1 bg-graphite-900 border-white/10 text-sm" />
              <button type="button" data-testid={`lp-faq-remove-${i}`} onClick={() => remove(i)}
                className="text-ink-secondary hover:text-accent-danger mt-1" aria-label="Remove Q/A">
                <X weight="bold" className="h-3.5 w-3.5" />
              </button>
            </div>
            <Textarea data-testid={`lp-faq-answer-${i}`} value={qa.answer || ""}
              onChange={e => patch(i, "answer", e.target.value)}
              rows={3} placeholder="Answer" maxLength={2000}
              className="bg-graphite-900 border-white/10 text-sm" />
          </li>
        ))}
      </ul>
      <button type="button" data-testid="lp-faq-add" onClick={add}
        className="inline-flex items-center gap-1.5 rounded-full bg-white text-graphite-950 px-3 py-1.5 text-xs font-medium hover:bg-neutral-100">
        <Plus weight="bold" className="h-3 w-3" /> Add pair
      </button>
    </div>
  );
}

// ── Media Phase 1 editor ────────────────────────────────────────────────
// Collapsed by default when no media configured — an "Add media" button
// expands it. Clearing sets `media` back to null so the public page
// renders exactly as before.
function MediaEditor({ sectionType, value, onChange }) {
  const active = !!value;
  if (!active) {
    return (
      <div className="pt-3 border-t border-white/5">
        <button type="button"
          data-testid={`lp-section-${sectionType}-media-add`}
          onClick={() => onChange({ type: "image", source: "", alt_text: "",
                                     placement: "below", display_style: "contained" })}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-graphite-950 text-ink-secondary hover:text-ink-primary hover:border-white/25 px-3 py-1.5 text-xs">
          <Plus weight="bold" className="h-3 w-3" /> Add media
        </button>
      </div>
    );
  }
  const patch = (p) => onChange({ ...value, ...p });
  const isImage = value.type === "image";
  const isIcon = value.type === "icon";
  const isGroup = value.type === "icon_group";
  const isVideo = value.type === "video_embedded";
  const isLogoStrip = value.type === "logo_strip";
  const isBeforeAfter = value.type === "before_after";
  const groupList = isGroup && Array.isArray(value.source) ? value.source : [];
  return (
    <div className="pt-3 border-t border-white/5 space-y-3" data-testid={`lp-section-${sectionType}-media`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
          <ImageIcon weight="duotone" className="h-3.5 w-3.5" /> Media
        </div>
        <button type="button"
          data-testid={`lp-section-${sectionType}-media-remove`}
          onClick={() => onChange(null)}
          className="text-[11px] text-ink-secondary hover:text-accent-danger inline-flex items-center gap-1">
          <X weight="bold" className="h-3 w-3" /> Remove
        </button>
      </div>
      <Field label="Type">
        <select
          data-testid={`lp-section-${sectionType}-media-type`}
          value={value.type}
          onChange={(e) => {
            const next = e.target.value;
            const isG = next === "icon_group";
            const isBA = next === "before_after";
            const isLS = next === "logo_strip";
            patch({
              type: next,
              source: isG ? [] : (isBA || isLS ? null : ""),
              items: isBA ? [{ label: "Before", url: "" }, { label: "After", url: "" }] : (isLS ? [] : null),
              thumbnail_url: next === "video_embedded" ? "" : null,
            });
          }}
          className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
          <option value="image">Image (URL)</option>
          <option value="video_embedded">Video (YouTube / Vimeo)</option>
          <option value="icon">Single icon</option>
          <option value="icon_group">Icon group</option>
          <option value="logo_strip">Logo strip</option>
          <option value="before_after">Before / After (2 images)</option>
        </select>
      </Field>
      {isVideo && (
        <>
          <Field label="Video URL (YouTube or Vimeo)" required>
            <Input data-testid={`lp-section-${sectionType}-media-source`}
              value={typeof value.source === "string" ? value.source : ""} maxLength={1000}
              onChange={(e) => patch({ source: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=…"
              className="bg-graphite-950 border-white/10" />
          </Field>
          <Field label="Thumbnail URL (optional — YouTube auto-derives)">
            <Input data-testid={`lp-section-${sectionType}-media-thumbnail`}
              value={value.thumbnail_url || ""} maxLength={1000}
              onChange={(e) => patch({ thumbnail_url: e.target.value })}
              placeholder="https://cdn.example.com/poster.jpg"
              className="bg-graphite-950 border-white/10" />
          </Field>
        </>
      )}
      {isLogoStrip && (
        <Field label="Client logos">
          <LogoStripItemsEditor
            testid={`lp-section-${sectionType}-media-logos`}
            items={Array.isArray(value.items) ? value.items : []}
            onChange={(items) => patch({ items })}
          />
        </Field>
      )}
      {isBeforeAfter && (
        <Field label="Before / After (both images required)">
          <BeforeAfterEditor
            testid={`lp-section-${sectionType}-media-ba`}
            items={Array.isArray(value.items) && value.items.length === 2
                    ? value.items : [{ label: "Before", url: "" }, { label: "After", url: "" }]}
            onChange={(items) => patch({ items })}
          />
        </Field>
      )}
      {isImage && (
        <>
          <Field label="Image URL" required>
            <Input data-testid={`lp-section-${sectionType}-media-source`}
              value={value.source || ""} maxLength={1000}
              onChange={(e) => patch({ source: e.target.value })}
              placeholder="https://cdn.example.com/hero.png"
              className="bg-graphite-950 border-white/10" />
          </Field>
          <Field label="Alt text (accessibility)">
            <Input data-testid={`lp-section-${sectionType}-media-alt`}
              value={value.alt_text || ""} maxLength={240}
              onChange={(e) => patch({ alt_text: e.target.value })}
              className="bg-graphite-950 border-white/10" />
          </Field>
        </>
      )}
      {isIcon && (
        <Field label="Icon">
          <IconPicker
            testid={`lp-section-${sectionType}-media-icon`}
            value={typeof value.source === "string" ? value.source : ""}
            onChange={(name) => patch({ source: name })}
          />
        </Field>
      )}
      {isGroup && (
        <Field label="Icons (choose up to 8)">
          <IconGroupPicker
            testid={`lp-section-${sectionType}-media-icons`}
            value={groupList}
            onChange={(arr) => patch({ source: arr })}
          />
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Placement">
          <select
            data-testid={`lp-section-${sectionType}-media-placement`}
            value={value.placement || "below"}
            onChange={(e) => patch({ placement: e.target.value })}
            className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
            {LP_MEDIA_PLACEMENTS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Display style">
          <select
            data-testid={`lp-section-${sectionType}-media-display-style`}
            value={value.display_style || "contained"}
            onChange={(e) => patch({ display_style: e.target.value })}
            className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
            {LP_MEDIA_DISPLAY_STYLES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </Field>
      </div>
    </div>
  );
}

function IconPicker({ testid, value, onChange }) {
  return (
    <div data-testid={testid} className="grid grid-cols-6 sm:grid-cols-10 gap-1.5 max-h-40 overflow-y-auto p-1 rounded-md border border-white/10 bg-graphite-950">
      {Object.entries(LP_MEDIA_ICONS).map(([name, Icon]) => {
        const active = value === name;
        return (
          <button key={name} type="button"
            data-testid={`${testid}-opt-${name}`}
            data-active={active ? "true" : "false"}
            onClick={() => onChange(name)}
            title={name}
            className={`h-8 w-8 flex items-center justify-center rounded ${active ? "bg-white text-graphite-950" : "text-ink-secondary hover:text-ink-primary hover:bg-white/5"}`}>
            <Icon weight={active ? "fill" : "duotone"} className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}

function IconGroupPicker({ testid, value, onChange }) {
  const toggle = (name) => {
    if (value.includes(name)) onChange(value.filter(n => n !== name));
    else if (value.length < 8) onChange([...value, name]);
  };
  return (
    <div data-testid={testid} className="grid grid-cols-6 sm:grid-cols-10 gap-1.5 max-h-40 overflow-y-auto p-1 rounded-md border border-white/10 bg-graphite-950">
      {Object.entries(LP_MEDIA_ICONS).map(([name, Icon]) => {
        const active = value.includes(name);
        return (
          <button key={name} type="button"
            data-testid={`${testid}-opt-${name}`}
            data-active={active ? "true" : "false"}
            onClick={() => toggle(name)}
            title={name}
            className={`h-8 w-8 flex items-center justify-center rounded ${active ? "bg-white text-graphite-950" : "text-ink-secondary hover:text-ink-primary hover:bg-white/5"}`}>
            <Icon weight={active ? "fill" : "duotone"} className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}



// ── Phase 2 editors ────────────────────────────────────────────────────

function StepsEditor({ steps, onChange }) {
  // Always-editable — no staging draft. Inputs bound directly to steps[i].
  const rows = steps.length > 0 ? steps : [{ title: "", description: "" }];
  const patch = (i, key, val) => {
    const base = steps.length > 0 ? steps : rows;
    onChange(base.map((s, idx) => idx === i ? { ...s, [key]: val } : s));
  };
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => {
    if (rows.length >= 6) return;
    onChange([...rows, { title: "", description: "" }]);
  };
  return (
    <div className="space-y-2" data-testid="lp-how-steps">
      <div className="text-[11px] uppercase tracking-widest text-ink-secondary">Steps (max 6)</div>
      <ul className="space-y-2">
        {rows.map((s, i) => (
          <li key={i} data-testid={`lp-how-step-${i}`}
              className="rounded-md border border-white/10 bg-graphite-950 p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs uppercase tracking-widest text-ink-secondary pt-1.5 shrink-0">
                Step {i + 1}
              </div>
              <button type="button" data-testid={`lp-how-step-${i}-remove`} onClick={() => remove(i)}
                className="text-ink-secondary hover:text-accent-danger" aria-label="Remove step">
                <X weight="bold" className="h-3.5 w-3.5" />
              </button>
            </div>
            <Input data-testid={`lp-how-step-${i}-title`} value={s.title || ""} maxLength={300}
              onChange={e => patch(i, "title", e.target.value)}
              placeholder="Step title (e.g. Book a call)"
              className="bg-graphite-900 border-white/10 text-sm" />
            <Textarea data-testid={`lp-how-step-${i}-desc`} value={s.description || ""} rows={2} maxLength={3000}
              onChange={e => patch(i, "description", e.target.value)}
              placeholder="Short description of what happens in this step"
              className="bg-graphite-900 border-white/10 text-sm" />
          </li>
        ))}
      </ul>
      {rows.length < 6 && (
        <button type="button" data-testid="lp-how-step-add" onClick={add}
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-graphite-950 px-3 py-1.5 text-xs font-medium hover:bg-neutral-100">
          <Plus weight="bold" className="h-3 w-3" /> Add step
        </button>
      )}
    </div>
  );
}

function LogoStripItemsEditor({ testid, items, onChange }) {
  // Always-editable pattern — inputs are bound DIRECTLY to items[i] fields,
  // no staging "draft" state. Previously this component required a separate
  // "Add logo" button click to commit the draft into `items`, which caused
  // silent data loss when users typed a URL and hit the outer Save button
  // without clicking Add first. Now every keystroke updates the array.
  const rows = items.length > 0 ? items : [{ name: "", url: "" }];
  const patch = (i, key, val) => {
    // If the user is typing into the seeded placeholder row (items === []),
    // materialise it into the real array on the first keystroke.
    const base = items.length > 0 ? items : rows;
    onChange(base.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  };
  const remove = (i) => {
    const next = rows.filter((_, idx) => idx !== i);
    onChange(next.length ? next : [{ name: "", url: "" }]);
  };
  const add = () => {
    if (rows.length >= 20) return;
    onChange([...rows, { name: "", url: "" }]);
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <ul className="space-y-2">
        {rows.map((l, i) => (
          <li key={i} data-testid={`${testid}-row-${i}`}
              className="rounded-md border border-white/10 bg-graphite-950 p-3">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2 items-center">
              <Input data-testid={`${testid}-name-${i}`} value={l.name || ""} maxLength={200}
                onChange={(e) => patch(i, "name", e.target.value)}
                placeholder="Client name (optional)"
                className="bg-graphite-900 border-white/10 text-sm" />
              <Input data-testid={`${testid}-url-${i}`} value={l.url || ""} maxLength={1000}
                onChange={(e) => patch(i, "url", e.target.value)}
                placeholder="https://.../logo.svg"
                className="bg-graphite-900 border-white/10 text-sm" />
              <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => remove(i)}
                className="text-ink-secondary hover:text-accent-danger justify-self-end sm:justify-self-center px-2"
                aria-label="Remove logo">
                <X weight="bold" className="h-3.5 w-3.5" />
              </button>
            </div>
          </li>
        ))}
      </ul>
      {rows.length < 20 && (
        <button type="button" data-testid={`${testid}-add`} onClick={add}
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-graphite-950 px-3 py-1.5 text-xs font-medium hover:bg-neutral-100">
          <Plus weight="bold" className="h-3 w-3" /> Add another logo
        </button>
      )}
    </div>
  );
}

function BeforeAfterEditor({ testid, items, onChange }) {
  const patch = (i, key, val) => {
    const next = items.map((it, idx) => idx === i ? { ...it, [key]: val } : it);
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="rounded-md border border-white/10 bg-graphite-950 p-3 space-y-2"
             data-testid={`${testid}-slot-${i}`}>
          <Input value={it.label || ""} maxLength={60}
            data-testid={`${testid}-label-${i}`}
            onChange={(e) => patch(i, "label", e.target.value)}
            placeholder={i === 0 ? "Before" : "After"}
            className="bg-graphite-900 border-white/10 text-sm" />
          <Input value={it.url || ""} maxLength={1000}
            data-testid={`${testid}-url-${i}`}
            onChange={(e) => patch(i, "url", e.target.value)}
            placeholder="https://cdn.example.com/before.png"
            className="bg-graphite-900 border-white/10 text-sm" />
        </div>
      ))}
    </div>
  );
}
